// DailyProductionReport.jsx
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import './DailyProductionReport.css';

function DailyProductionReport() {
  const [dailyData, setDailyData] = useState([]);
  const [articleShiftData, setArticleShiftData] = useState([]); // mensual: por empleado + día + turno + artículo

  const [employeeTipoFilter, setEmployeeTipoFilter] = useState('All');
  const [allEmployeeTypes, setAllEmployeeTypes] = useState([]);

  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('lastShift'); // 'lastShift' | 'articleMonth'

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Cargar datos de ambos endpoints
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [resLastShift, resArticleMonth] = await Promise.all([
          axios.get('/api/production/employee-last-shift-production'),
          axios.get('/api/production/article-shifts-last-month'),
        ]);

        // Normalizar datos de último turno por empleado
        const normalized = resLastShift.data.map(emp => {
          const rawLastShift = emp.last_shift || null;
          const rawLastInterval = emp.last_interval || null;

          let last_shift = null;
          if (rawLastShift) {
            const worked =
              parseFloat(
                rawLastShift.worked_hours ??
                  rawLastShift.duration_in_hours ??
                  0
              ) || 0;

            const intervals = Array.isArray(rawLastShift.intervals)
              ? rawLastShift.intervals.map(it => ({
                  ...it,
                  duration_in_hours_num:
                    typeof it.duration_in_hours === 'number'
                      ? it.duration_in_hours
                      : parseFloat(it.duration_in_hours ?? 0) || 0,
                }))
              : [];

            last_shift = {
              ...rawLastShift,
              worked_hours_num: worked,
              intervals,
            };
          }

          let last_interval = null;
          if (rawLastInterval) {
            last_interval = {
              ...rawLastInterval,
              duration_in_hours:
                typeof rawLastInterval.duration_in_hours === 'number'
                  ? rawLastInterval.duration_in_hours
                  : parseFloat(rawLastInterval.duration_in_hours || 0) || 0,
            };
          }

          return {
            ...emp,
            last_shift,
            last_interval,
          };
        });

        setDailyData(normalized);

        const tipos = Array.from(
          new Set(normalized.map(e => e.employee_tipo).filter(Boolean))
        );
        setAllEmployeeTypes(tipos);

        // Normalizar datos de producción mensual por empleado + día + turno + artículo
        const articleDataNorm = (resArticleMonth.data || []).map(row => ({
          ...row,
          total_worked_hours:
            typeof row.total_worked_hours === 'number'
              ? row.total_worked_hours
              : parseFloat(row.total_worked_hours ?? 0) || 0,
        }));
        setArticleShiftData(articleDataNorm);

        setLoading(false);
      } catch (err) {
        console.error(err);
        setError('Error al cargar los datos.');
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Turno para la vista del último turno
  const getShiftLabel = emp => {
    if (emp.last_shift && emp.last_shift.shift_type) return emp.last_shift.shift_type;
    if (emp.last_interval && emp.last_interval.start_time) {
      const startTime = emp.last_interval.start_time;
      const parts = startTime.split(' ');
      if (parts.length < 2) return 'Desconocido';
      const [hour, minute] = parts[1].split(':').map(Number);
      const totalMinutes = hour * 60 + minute;
      if (totalMinutes < 6 * 60) return 'Noche';
      if (totalMinutes < 14 * 60) return 'Mañana';
      if (totalMinutes < 22 * 60) return 'Tarde';
      return 'Noche';
    }
    return 'Desconocido';
  };

  // Filtro vista por empleado (último turno)
  const filteredEmployees = dailyData
    .filter(emp =>
      employeeTipoFilter === 'All' ? true : emp.employee_tipo === employeeTipoFilter
    )
    .filter(emp =>
      emp.employee_name
        ? emp.employee_name.toLowerCase().includes(searchTerm.toLowerCase())
        : false
    )
    .sort((a, b) => a.employee_name.localeCompare(b.employee_name, 'es'));

  // Filtro vista mensual (busca por empleado o artículo)
  const filteredArticleShiftData = articleShiftData
    .filter(row => {
      if (!searchTerm) return true;
      const term = searchTerm.toLowerCase();
      const art = (row.article_name || '').toLowerCase();
      const emp = (row.employee_name || '').toLowerCase();
      return art.includes(term) || emp.includes(term);
    })
    .sort((a, b) => {
      // Orden: empleado -> fecha (desc) -> artículo
      const empCmp = (a.employee_name || '').localeCompare(
        b.employee_name || '',
        'es'
      );
      if (empCmp !== 0) return empCmp;

      const dateA = a.shift_date || '';
      const dateB = b.shift_date || '';
      if (dateA !== dateB) return dateB.localeCompare(dateA); // más reciente primero

      return (a.article_name || '').localeCompare(
        b.article_name || '',
        'es'
      );
    });

  // Agrupar mensual por empleado para mostrarlo como quieres: cada empleado -> tabla con días y artículos
  const monthlyByEmployee = filteredArticleShiftData.reduce((acc, row) => {
    const key = row.employee_name || 'Desconocido';
    if (!acc[key]) acc[key] = [];
    acc[key].push(row);
    return acc;
  }, {});

  const formatDateTime = iso => {
    if (!iso) return '-';
    const d = new Date(iso);
    if (isNaN(d)) return iso;
    return `${d.toLocaleDateString('es-ES')} ${d.toLocaleTimeString('es-ES', {
      hour: '2-digit',
      minute: '2-digit',
    })}`;
  };

  const formatDate = d => {
    if (!d) return '-';
    // d viene como 'YYYY-MM-DD'
    const parts = d.split('-');
    if (parts.length === 3) {
      const [year, month, day] = parts;
      return `${day}/${month}/${year}`;
    }
    return d;
  };

  // ---- EXPORT PDF: vista último turno ----
  const exportLastShiftPDF = () => {
    const pdf = new jsPDF('portrait', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const margin = 15;
    let yOffset = margin;

    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(18);
    pdf.text('Producción por artículo (último turno)', pageWidth / 2, yOffset, {
      align: 'center',
    });
    yOffset += 8;

    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    pdf.text(
      `Fecha: ${new Date().toLocaleDateString(
        'es-ES'
      )} - Hora: ${new Date().toLocaleTimeString('es-ES')}`,
      pageWidth - margin,
      yOffset,
      { align: 'right' }
    );
    yOffset += 5;
    pdf.line(margin, yOffset, pageWidth - margin, yOffset);
    yOffset += 8;

    filteredEmployees.forEach((emp, index) => {
      const ls = emp.last_shift;
      const li = emp.last_interval;
      const articles = emp.production_by_article || [];

      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(12);
      pdf.text(
        `${emp.employee_name} (${emp.employee_tipo || 'Sin tipo'})`,
        margin,
        yOffset
      );
      yOffset += 6;

      if (ls || li) {
        const shiftType = getShiftLabel(emp);
        const start = ls?.start_time || li?.start_time;
        const end = ls?.end_time || li?.end_time;
        const hours =
          ls?.worked_hours_num ??
          ls?.worked_hours ??
          li?.duration_in_hours ??
          null;

        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(10);

        const shiftDateText = ls?.shift_date
          ? `Fecha turno: ${ls.shift_date}  |  `
          : '';

        pdf.text(
          `Turno: ${shiftType}  |  ${shiftDateText}Inicio: ${formatDateTime(
            start
          )}  |  Fin: ${formatDateTime(end)}`,
          margin,
          yOffset
        );
        yOffset += 6;

        pdf.text(
          `Horas trabajadas en el turno: ${
            hours !== null && hours !== undefined
              ? parseFloat(hours).toFixed(2) + ' h'
              : '-'
          }`,
          margin,
          yOffset
        );
        yOffset += 5;

        const alert = ls?.alert || li?.alert;
        pdf.text(
          `Alerta: ${alert === 'green' ? '✅ Correcto' : '❌ Revisar'}`,
          margin,
          yOffset
        );
        yOffset += 5;
      } else {
        pdf.setFont('helvetica', 'italic');
        pdf.setFontSize(10);
        pdf.text('Sin último turno registrado.', margin, yOffset);
        yOffset += 6;
      }

      if (articles.length > 0) {
        pdf.autoTable({
          startY: yOffset,
          head: [['ID Artículo', 'Nombre', 'Cantidad total', 'Semielab.']],
          body: articles.map(a => [
            a.article_id,
            a.article_name || '',
            a.total_amount,
            a.semielaborado === 1 ? 'Sí' : 'No',
          ]),
          margin: { left: margin, right: margin },
          styles: { fontSize: 9 },
          headStyles: { fillColor: [230, 230, 230] },
        });
        yOffset = pdf.lastAutoTable.finalY + 6;
      } else {
        pdf.setFont('helvetica', 'italic');
        pdf.setFontSize(9);
        pdf.text('Sin producción en el último turno.', margin, yOffset);
        yOffset += 6;
      }

      if (index < filteredEmployees.length - 1) {
        pdf.setDrawColor(200);
        pdf.line(margin, yOffset, pageWidth - margin, yOffset);
        yOffset += 6;
      }

      if (yOffset > pdf.internal.pageSize.getHeight() - 20) {
        pdf.addPage();
        yOffset = margin;
      }
    });

    pdf.save('produccion_ultimo_turno.pdf');
  };

  // ---- EXPORT PDF: vista mensual (cada día, cada empleado, por artículo) ----
  const exportArticleMonthPDF = () => {
    const pdf = new jsPDF('portrait', 'mm', 'a4');
    const pageWidth = pdf.internal.pageSize.getWidth();
    const margin = 15;
    let yOffset = margin;

    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(18);
    pdf.text(
      'Producción mensual por empleado, día y artículo',
      pageWidth / 2,
      yOffset,
      { align: 'center' }
    );
    yOffset += 8;

    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(10);
    pdf.text(
      `Fecha: ${new Date().toLocaleDateString(
        'es-ES'
      )} - Hora: ${new Date().toLocaleTimeString('es-ES')}`,
      pageWidth - margin,
      yOffset,
      { align: 'right' }
    );
    yOffset += 5;
    pdf.line(margin, yOffset, pageWidth - margin, yOffset);
    yOffset += 8;

    Object.entries(monthlyByEmployee).forEach(([employeeName, rows], idxEmp) => {
      const tipo = rows[0]?.employee_tipo || 'Sin tipo';

      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(12);
      pdf.text(`${employeeName} (${tipo})`, margin, yOffset);
      yOffset += 6;

      const body = rows.map(r => [
        r.shift_date || '',
        r.shift_type || '',
        r.article_id,
        r.article_name || '',
        r.total_amount,
        r.semielaborado === 1 ? 'Sí' : 'No',
        r.total_worked_hours
          ? r.total_worked_hours.toFixed(2)
          : '0.00',
      ]);

      pdf.autoTable({
        startY: yOffset,
        head: [[
          'Fecha',
          'Turno',
          'ID Artículo',
          'Artículo',
          'Cantidad',
          'Semielab.',
          'Horas turno',
        ]],
        body,
        margin: { left: margin, right: margin },
        styles: { fontSize: 9 },
        headStyles: { fillColor: [230, 230, 230] },
      });

      yOffset = pdf.lastAutoTable.finalY + 8;

      if (idxEmp < Object.keys(monthlyByEmployee).length - 1) {
        pdf.setDrawColor(200);
        pdf.line(margin, yOffset, pageWidth - margin, yOffset);
        yOffset += 6;
      }

      if (yOffset > pdf.internal.pageSize.getHeight() - 30) {
        pdf.addPage();
        yOffset = margin;
      }
    });

    pdf.save('produccion_mensual_empleado_dia_articulo.pdf');
  };

  const exportToPDF = () => {
    if (viewMode === 'lastShift') {
      exportLastShiftPDF();
    } else {
      exportArticleMonthPDF();
    }
  };

  if (loading) return <p className="loading">Cargando datos...</p>;
  if (error) return <p className="error">{error}</p>;

  return (
    <div className="container">
      <div className="controls">
        {/* Botones de vista */}
        <div className="buttons-group">
          <button
            onClick={() => setViewMode('lastShift')}
            className={viewMode === 'lastShift' ? 'active' : ''}
          >
            Último turno por empleado
          </button>
          <button
            onClick={() => setViewMode('articleMonth')}
            className={viewMode === 'articleMonth' ? 'active' : ''}
          >
            Mensual por empleado / día / artículo
          </button>

          <button onClick={exportToPDF}>Exportar a PDF</button>
        </div>

        {/* Filtros de tipo de empleado (solo en vista por empleado) */}
        {viewMode === 'lastShift' && (
          <div className="employee-type-filters">
            <button
              onClick={() => setEmployeeTipoFilter('All')}
              className={employeeTipoFilter === 'All' ? 'active' : ''}
            >
              Todos
            </button>
            {allEmployeeTypes.map(tipo => (
              <button
                key={tipo}
                onClick={() => setEmployeeTipoFilter(tipo)}
                className={employeeTipoFilter === tipo ? 'active' : ''}
              >
                {tipo}
              </button>
            ))}
          </div>
        )}

        {/* Buscador */}
        <input
          type="text"
          placeholder={
            viewMode === 'lastShift'
              ? 'Buscar empleado...'
              : 'Buscar artículo o empleado...'
          }
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          className="search-input"
        />
      </div>

      <div className="report-content">
        {viewMode === 'lastShift' ? (
          // -------- VISTA 1: ÚLTIMO TURNO POR EMPLEADO --------
          filteredEmployees.map((emp, idx) => {
            const ls = emp.last_shift;
            const li = emp.last_interval;
            const articles = emp.production_by_article || [];
            const shift = getShiftLabel(emp);

            const start = ls?.start_time || li?.start_time;
            const end = ls?.end_time || li?.end_time;
            const hours =
              ls?.worked_hours_num ??
              ls?.worked_hours ??
              li?.duration_in_hours ??
              null;
            const alert = ls?.alert || li?.alert;

            return (
              <div
                key={emp.employee_code}
                className={`employee-section ${
                  idx % 2 === 0 ? 'black-border' : 'gray-border'
                }`}
              >
                <h3>
                  {emp.employee_name}{' '}
                  <span className="employee-tipo">({emp.employee_tipo})</span>
                </h3>

                {ls || li ? (
                  <div className="shift-info">
                    <p>
                      <strong>Turno:</strong> {shift}{' '}
                      {alert === 'green' ? (
                        <span className="badge badge-green">✅ OK</span>
                      ) : (
                        <span className="badge badge-red">❌ Revisar</span>
                      )}
                    </p>
                    {ls?.shift_date && (
                      <p>
                        <strong>Fecha del turno:</strong> {ls.shift_date}
                      </p>
                    )}
                    <p>
                      <strong>Inicio:</strong> {formatDateTime(start)} &nbsp;&nbsp;
                      <strong>Fin:</strong> {formatDateTime(end)}
                    </p>
                    <p>
                      <strong>Horas trabajadas en el turno:</strong>{' '}
                      {hours !== null && hours !== undefined
                        ? `${parseFloat(hours).toFixed(2)} h`
                        : '-'}
                    </p>

                    {/* Detalle de intervalos del turno */}
                    {ls && ls.intervals && ls.intervals.length > 0 && (
                      <div style={{ marginTop: '6px', fontSize: '12px' }}>
                        <strong>Intervalos del turno:</strong>
                        <table style={{ marginTop: '4px' }}>
                          <thead>
                            <tr>
                              <th>Inicio</th>
                              <th>Fin</th>
                              <th>Duración (h)</th>
                            </tr>
                          </thead>
                          <tbody>
                            {ls.intervals.map((iv, i) => (
                              <tr key={i}>
                                <td>{formatDateTime(iv.start_time)}</td>
                                <td>{formatDateTime(iv.end_time)}</td>
                                <td>
                                  {iv.duration_in_hours_num !== undefined
                                    ? iv.duration_in_hours_num.toFixed(2)
                                    : iv.duration_in_hours}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="no-shift">Sin último turno registrado.</p>
                )}

                <div className="day-section">
                  <h4>Producción por artículo en el último turno</h4>
                  {articles.length > 0 ? (
                    <table>
                      <thead>
                        <tr>
                          <th>ID Artículo</th>
                          <th>Nombre</th>
                          <th>Cantidad total</th>
                          <th>Semielab.</th>
                        </tr>
                      </thead>
                      <tbody>
                        {articles.map((a, i) => (
                          <tr key={i}>
                            <td>{a.article_id}</td>
                            <td>{a.article_name}</td>
                            <td>{a.total_amount}</td>
                            <td>{a.semielaborado === 1 ? 'Sí' : 'No'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <p className="no-production">
                      No hay producción registrada en este turno.
                    </p>
                  )}
                </div>
              </div>
            );
          })
        ) : (
          // -------- VISTA 2: MENSUAL POR EMPLEADO / DÍA / ARTÍCULO --------
          <div className="article-month-section">
            {Object.keys(monthlyByEmployee).length === 0 ? (
              <p className="no-production">
                No hay producción registrada en el último mes.
              </p>
            ) : (
              Object.entries(monthlyByEmployee).map(([employeeName, rows], idx) => {
                const tipo = rows[0]?.employee_tipo || 'Sin tipo';

                return (
                  <div
                    key={employeeName}
                    className={`employee-section ${
                      idx % 2 === 0 ? 'black-border' : 'gray-border'
                    }`}
                  >
                    <h3>
                      {employeeName}{' '}
                      <span className="employee-tipo">({tipo})</span>
                    </h3>

                    <div className="day-section" style={{ marginTop: '10px' }}>
                      <h4>Producción diaria por artículo (último mes)</h4>
                      <table>
                        <thead>
                          <tr>
                            <th>Fecha</th>
                            <th>Turno</th>
                            <th>ID Artículo</th>
                            <th>Artículo</th>
                            <th>Cantidad</th>
                            <th>Semielab.</th>
                            <th>Horas turno</th>
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((r, i) => (
                            <tr key={i}>
                              <td>{formatDate(r.shift_date)}</td>
                              <td>{r.shift_type}</td>
                              <td>{r.article_id}</td>
                              <td>{r.article_name}</td>
                              <td>{r.total_amount}</td>
                              <td>{r.semielaborado === 1 ? 'Sí' : 'No'}</td>
                              <td>
                                {r.total_worked_hours
                                  ? r.total_worked_hours.toFixed(2)
                                  : '0.00'}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default DailyProductionReport;
